/**
 * @fileoverview ImportTakeoutModal Component
 * Modal for importing restaurants from Google Takeout export into a new list
 */

import React, { useState, useCallback } from 'react';
import { MapPin, Check, AlertCircle, FileUp } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSaveRestaurant } from '../../restaurants/hooks/use-save-restaurant';
import { GooglePlacesRestaurant } from '../../restaurants/types/restaurants.types';
import { restaurantsService } from '../../restaurants/services/restaurants.service';
import { useCreateList, useAddToList } from '../hooks/use-lists';
import { CITY_OPTIONS } from '@/constants/cities';
import { client } from '@/lib/hono';

interface ImportTakeoutModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TakeoutPlace {
  title: string;
  address?: string;
  latitude: number;
  longitude: number;
  googleMapsUrl?: string;
  // Fields from Google Places API lookup
  realPlaceId?: string;
  rating?: number;
  priceLevel?: number;
  types?: string[];
  website?: string;
}

interface GeoJsonFeature {
  type: string;
  geometry?: {
    type: string;
    coordinates?: number[];
  };
  properties?: {
    Title?: string;
    name?: string;
    Address?: string;
    address?: string;
    'Google Maps URL'?: string;
    url?: string;
  };
}

interface GeoJsonData {
  type?: string;
  features?: GeoJsonFeature[];
}

interface PlaceData {
  title?: string;
  name?: string;
  address?: string;
  geo?: { latitude: number; longitude: number };
  latitude?: number;
  longitude?: number;
  url?: string;
  googleMapsUrl?: string;
}

type ImportStep = 'upload' | 'resolving' | 'listName' | 'select' | 'importing' | 'complete';

export const ImportTakeoutModal: React.FC<ImportTakeoutModalProps> = ({
  isOpen,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<ImportStep>('upload');
  const [places, setPlaces] = useState<TakeoutPlace[]>([]);
  const [selectedPlaces, setSelectedPlaces] = useState<Set<number>>(new Set());
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });
  const [error, setError] = useState<string | null>(null);
  const [resolveProgress, setResolveProgress] = useState({ current: 0, total: 0, resolved: 0 });

  // List creation state
  const [listName, setListName] = useState('');
  const [city, setCity] = useState('');
  const [createdListId, setCreatedListId] = useState<string | null>(null);

  const saveRestaurant = useSaveRestaurant();
  const createList = useCreateList();
  const addToList = useAddToList();

  // Extract CID from Google Maps URL and convert to decimal
  // URL format: data=!4m2!3m1!1s0x5495a0a60fccc01d:0x727d25ac630f210b
  // The SECOND part (after colon) is the CID in hex, API needs decimal
  const extractCidFromUrl = useCallback((url: string): string | null => {
    if (!url) return null;
    // Match the hex CID pattern - extract the second part after the colon
    const match = url.match(/!1s0x[a-f0-9]+:0x([a-f0-9]+)/i);
    if (!match?.[1]) return null;

    // Convert hex to decimal (BigInt needed for 64-bit values)
    try {
      return BigInt('0x' + match[1]).toString();
    } catch {
      return null;
    }
  }, []);

  // Lookup place details by CID using backend endpoint
  interface PlaceDetails {
    lat: number;
    lng: number;
    address?: string;
    placeId?: string;
    name?: string;
    rating?: number;
    priceLevel?: number;
    types?: string[];
    website?: string;
  }

  const lookupPlaceByCid = useCallback(async (cid: string): Promise<PlaceDetails | null> => {
    try {
      const response = await client.api.places['lookup-cid'].$post({
        json: { cid },
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();

      if (result.success && result.data) {
        const data = result.data;
        return {
          lat: data.geometry?.location?.lat ?? 0,
          lng: data.geometry?.location?.lng ?? 0,
          address: data.formatted_address,
          placeId: data.place_id,
          name: data.name,
          rating: data.rating,
          priceLevel: data.price_level,
          types: data.types,
          website: data.website,
        };
      }

      return null;
    } catch (err) {
      console.error('CID lookup failed:', err);
      return null;
    }
  }, []);

  // Resolve place details using CID from Google Maps URL
  const resolveCoordinates = useCallback(async (parsedPlaces: TakeoutPlace[]) => {
    // Find places with a Google Maps URL containing a CID
    const placesWithUrls = parsedPlaces.filter(p => p.googleMapsUrl);

    if (placesWithUrls.length === 0) {
      // No URLs to lookup, go to list name step
      setPlaces(parsedPlaces);
      setSelectedPlaces(new Set(parsedPlaces.map((_, i) => i)));
      setStep('listName');
      return;
    }

    setStep('resolving');
    setResolveProgress({ current: 0, total: placesWithUrls.length, resolved: 0 });

    const updatedPlaces = [...parsedPlaces];
    let resolvedCount = 0;
    let processedCount = 0;

    for (let i = 0; i < parsedPlaces.length; i++) {
      const place = parsedPlaces[i];
      if (!place?.googleMapsUrl) continue;

      try {
        // Extract CID from URL
        const cid = extractCidFromUrl(place.googleMapsUrl);

        if (cid) {
          // Lookup place by CID using backend endpoint
          const details = await lookupPlaceByCid(cid);
          if (details && details.placeId) {
            updatedPlaces[i] = {
              ...place,
              // Use exact data from Google Places API
              latitude: details.lat,
              longitude: details.lng,
              address: details.address ?? place.address,
              // Store the real Place ID and all fields from API
              realPlaceId: details.placeId,
              rating: details.rating,
              priceLevel: details.priceLevel,
              types: details.types,
              website: details.website,
            };
            resolvedCount++;
          }
        }
      } catch (err) {
        console.error(`Failed to resolve details for ${place.title}:`, err);
      }

      processedCount++;
      setResolveProgress({
        current: processedCount,
        total: placesWithUrls.length,
        resolved: resolvedCount
      });

      // Small delay to avoid rate limiting
      await new Promise(r => setTimeout(r, 150));
    }

    setPlaces(updatedPlaces);
    setSelectedPlaces(new Set(updatedPlaces.map((_, i) => i)));
    setStep('listName');
  }, [extractCidFromUrl, lookupPlaceByCid]);

  // Extract coordinates from Google Maps URL
  const extractCoordsFromUrl = useCallback((url: string): { lat: number; lng: number } | null => {
    if (!url) return null;

    const patterns = [
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
      /ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match?.[1] && match[2]) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return { lat, lng };
        }
      }
    }

    return null;
  }, []);

  // Parse CSV content
  const parseCsv = useCallback((content: string): TakeoutPlace[] => {
    const cleanContent = content.replace(/^\ufeff/, ''); // Strip BOM
    const lines = cleanContent.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file appears to be empty or has no data rows');
    }

    const headerLine = lines[0];
    if (!headerLine) {
      throw new Error('CSV file is missing header row');
    }
    const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

    const titleIndex = headers.findIndex(h => h === 'title' || h === 'name');
    const urlIndex = headers.findIndex(h => h === 'url' || h === 'google maps url' || h === 'link');
    const addressIndex = headers.findIndex(h => h === 'address' || h === 'location');

    if (titleIndex === -1) {
      throw new Error('CSV must have a "Title" or "Name" column');
    }

    const places: TakeoutPlace[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i]?.trim();
      if (!line) continue;

      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const title = values[titleIndex]?.replace(/^"|"$/g, '');
      const url = urlIndex !== -1 ? values[urlIndex]?.replace(/^"|"$/g, '') : undefined;
      const address = addressIndex !== -1 ? values[addressIndex]?.replace(/^"|"$/g, '') : undefined;

      if (!title) continue;

      const coords = url ? extractCoordsFromUrl(url) : null;

      if (coords) {
        places.push({
          title,
          address,
          latitude: coords.lat,
          longitude: coords.lng,
          googleMapsUrl: url,
        });
      } else if (url) {
        places.push({
          title,
          address,
          latitude: 0,
          longitude: 0,
          googleMapsUrl: url,
        });
      }
    }

    return places;
  }, [extractCoordsFromUrl]);

  // Parse Google Takeout JSON file
  const parseGeoJson = useCallback((content: string): TakeoutPlace[] => {
    try {
      const data = JSON.parse(content);

      const geoData = data as GeoJsonData;
      if (geoData.type === 'FeatureCollection' && Array.isArray(geoData.features)) {
        return geoData.features
          .filter((feature: GeoJsonFeature) =>
            feature.type === 'Feature' &&
            feature.geometry?.type === 'Point' &&
            feature.geometry?.coordinates?.length !== undefined &&
            feature.geometry.coordinates.length >= 2
          )
          .map((feature: GeoJsonFeature): TakeoutPlace => ({
            title: feature.properties?.Title ?? feature.properties?.name ?? 'Unknown',
            address: feature.properties?.Address ?? feature.properties?.address,
            longitude: feature.geometry?.coordinates?.[0] ?? 0,
            latitude: feature.geometry?.coordinates?.[1] ?? 0,
            googleMapsUrl: feature.properties?.['Google Maps URL'] ?? feature.properties?.url,
          }));
      }

      if (Array.isArray(data)) {
        return (data as PlaceData[])
          .filter((place: PlaceData) => place.geo || (place.latitude && place.longitude))
          .map((place: PlaceData): TakeoutPlace => ({
            title: place.title ?? place.name ?? 'Unknown',
            address: place.address,
            latitude: place.geo?.latitude ?? place.latitude ?? 0,
            longitude: place.geo?.longitude ?? place.longitude ?? 0,
            googleMapsUrl: place.url ?? place.googleMapsUrl,
          }));
      }

      throw new Error('Unrecognized file format');
    } catch {
      throw new Error('Failed to parse file. Please ensure it\'s a valid Google Takeout export.');
    }
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const isCSV = file.name.toLowerCase().endsWith('.csv');

        const parsedPlaces = isCSV ? parseCsv(content) : parseGeoJson(content);

        if (parsedPlaces.length === 0) {
          setError('No places found in the file. Please check the file format.');
          return;
        }

        // Try to resolve coordinates for places that don't have them
        resolveCoordinates(parsedPlaces);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse file');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  }, [parseGeoJson, parseCsv, resolveCoordinates]);

  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    const isValidFile = file && (
      file.type === 'application/json' ||
      file.type === 'text/csv' ||
      file.name.endsWith('.json') ||
      file.name.endsWith('.csv')
    );

    if (isValidFile) {
      const input = document.createElement('input');
      input.type = 'file';
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleFileUpload({ target: input } as React.ChangeEvent<HTMLInputElement>);
    } else {
      setError('Please upload a JSON or CSV file');
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  // Toggle place selection
  const togglePlace = useCallback((index: number) => {
    setSelectedPlaces(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Select/deselect all
  const toggleAll = useCallback(() => {
    if (selectedPlaces.size === places.length) {
      setSelectedPlaces(new Set());
    } else {
      setSelectedPlaces(new Set(places.map((_, i) => i)));
    }
  }, [places, selectedPlaces.size]);

  // Import selected places
  const handleImport = useCallback(async () => {
    const placesToImport = places.filter((_, i) => selectedPlaces.has(i));

    setStep('importing');
    setImportProgress({ current: 0, total: placesToImport.length });
    setImportResults({ success: 0, failed: 0 });

    // First, create the list
    let listId: string;
    try {
      const listResponse = await createList.mutateAsync({
        listName: listName.trim(),
        listType: 'custom',
        city: city || undefined,
      });
      listId = listResponse.data.id;
      setCreatedListId(listId);
    } catch (err) {
      console.error('Failed to create list:', err);
      setError('Failed to create list. Please try again.');
      setStep('listName');
      return;
    }

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < placesToImport.length; i++) {
      const place = placesToImport[i];
      if (!place) continue;

      // Skip places without a valid Google Place ID
      if (!place.realPlaceId) {
        console.warn(`Skipping ${place.title} - no valid Google Place ID`);
        failedCount++;
        setImportProgress({ current: i + 1, total: placesToImport.length });
        setImportResults({ success: successCount, failed: failedCount });
        continue;
      }

      try {
        let restaurantId: string;

        // Check if restaurant already exists by placeId
        try {
          const checkResult = await restaurantsService.checkRestaurantSaved(place.realPlaceId);
          if (checkResult.data?.restaurantId) {
            // Restaurant already exists - just add to list
            restaurantId = checkResult.data.restaurantId;
            await addToList.mutateAsync({ listId, restaurantId });
            successCount++;
            setImportProgress({ current: i + 1, total: placesToImport.length });
            setImportResults({ success: successCount, failed: failedCount });
            continue;
          }
        } catch {
          // Check failed, continue with save
        }

        // Create Google Places format with all available fields
        // Note: photos are not passed from CID lookup as they're in REST API format (photo_reference)
        // and would need separate conversion to URLs
        const googlePlaceFormat: GooglePlacesRestaurant = {
          place_id: place.realPlaceId,
          name: place.title,
          formatted_address: place.address ?? `${place.latitude}, ${place.longitude}`,
          geometry: {
            location: {
              lat: place.latitude,
              lng: place.longitude,
            },
          },
          types: place.types || ['restaurant'],
          rating: place.rating,
          price_level: place.priceLevel,
          website: place.website,
        };

        // Save the restaurant and get the saved restaurant ID
        const savedResult = await saveRestaurant.mutateAsync(googlePlaceFormat);
        restaurantId = savedResult.data.restaurantId;

        // Add to the list
        await addToList.mutateAsync({ listId, restaurantId });

        successCount++;
      } catch (err) {
        console.error(`Failed to import ${place.title}:`, err);
        failedCount++;
      }

      setImportProgress({ current: i + 1, total: placesToImport.length });
      setImportResults({ success: successCount, failed: failedCount });
    }

    setStep('complete');
  }, [places, selectedPlaces, listName, city, createList, saveRestaurant, addToList]);

  // Navigate to the created list
  const handleViewList = useCallback(() => {
    if (createdListId) {
      onOpenChange(false);
      navigate({ to: '/dashboard/lists/detail/$listId', params: { listId: createdListId } });
    }
  }, [createdListId, navigate, onOpenChange]);

  // Reset modal state
  const handleClose = useCallback(() => {
    setStep('upload');
    setPlaces([]);
    setSelectedPlaces(new Set());
    setImportProgress({ current: 0, total: 0 });
    setImportResults({ success: 0, failed: 0 });
    setResolveProgress({ current: 0, total: 0, resolved: 0 });
    setListName('');
    setCity('');
    setCreatedListId(null);
    setError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Import from Google Takeout</DialogTitle>
          <DialogDescription>
            Import your saved places from a Google Takeout export file into a new list.
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-4 py-4">
            <div
              className="border-2 border-dashed rounded-sm p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => document.getElementById('takeout-file-input')?.click()}
            >
              <input
                id="takeout-file-input"
                type="file"
                accept=".json,.csv,application/json,text/csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <FileUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm font-medium mb-1">
                Drop your Takeout file here
              </p>
              <p className="text-xs text-muted-foreground">
                Supports CSV and JSON formats
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-brand text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <Card className="p-4 bg-muted/50">
              <h4 className="font-medium mb-2">How to export from Google Takeout:</h4>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Go to <a href="https://takeout.google.com" target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-[3px]">takeout.google.com</a></li>
                <li>Click "Deselect all" then select only "Saved"</li>
                <li>Click "Next step" and choose your export format</li>
                <li>Download and unzip the file</li>
                <li>Upload the CSV or JSON file from the "Saved" folder</li>
              </ol>
            </Card>
          </div>
        )}

        {/* Step 2: Resolving coordinates */}
        {step === 'resolving' && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="font-medium mb-2">
                Looking up coordinates...
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {resolveProgress.current} of {resolveProgress.total} places processed
                {resolveProgress.resolved > 0 && ` (${resolveProgress.resolved} found)`}
              </p>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${resolveProgress.total > 0 ? (resolveProgress.current / resolveProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Enter list name */}
        {step === 'listName' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Found {places.length} places. Enter a name for your new list.
            </p>

            <div className="space-y-2">
              <Label htmlFor="listName">List Name</Label>
              <Input
                id="listName"
                placeholder="e.g., Portland Favorites, Cafes to Try"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City (Optional)</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {CITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-brand text-sm">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button
                onClick={() => setStep('select')}
                disabled={!listName.trim()}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Select places */}
        {step === 'select' && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Select which places to import into "{listName}".
              </p>
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {selectedPlaces.size === places.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            {places.some(p => p.latitude === 0 && p.longitude === 0) && (
              <div className="flex items-start gap-2 p-3 bg-muted border border-border rounded-sm text-sm">
                <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-muted-foreground">
                  Some places don't have map coordinates. They will be saved by name but won't appear on the map view.
                </p>
              </div>
            )}

            <ScrollArea className="h-[280px] rounded-md border p-2">
              <div className="space-y-2">
                {places.map((place, index) => {
                  const hasCoords = place.latitude !== 0 || place.longitude !== 0;
                  return (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-sm cursor-pointer transition-colors ${
                        selectedPlaces.has(index) ? 'bg-primary/10' : 'hover:bg-muted'
                      }`}
                      onClick={() => togglePlace(index)}
                    >
                      <Checkbox
                        checked={selectedPlaces.has(index)}
                        onCheckedChange={() => togglePlace(index)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{place.title}</p>
                        {place.address && (
                          <p className="text-sm text-muted-foreground truncate">
                            {place.address}
                          </p>
                        )}
                      </div>
                      <MapPin className={`h-4 w-4 flex-shrink-0 ${hasCoords ? 'text-foreground' : 'text-muted-foreground'}`} />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('listName')}>
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={selectedPlaces.size === 0}
              >
                Import {selectedPlaces.size} {selectedPlaces.size === 1 ? 'Place' : 'Places'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Importing */}
        {step === 'importing' && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="font-medium">
                Importing places to "{listName}"... ({importProgress.current}/{importProgress.total})
              </p>
              <div className="w-full bg-muted rounded-full h-2 mt-4">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Complete */}
        {step === 'complete' && (
          <div className="space-y-4 py-8">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Check className="h-6 w-6 text-foreground" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Import Complete!</h3>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p className="text-foreground">
                  {importResults.success} places imported to "{listName}"
                </p>
                {importResults.failed > 0 && (
                  <p className="text-brand">
                    {importResults.failed} places failed to import
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={handleClose}>
                Done
              </Button>
              <Button onClick={handleViewList}>
                View List
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
